import { Staff } from "./IAuth";
import { CompactServiceUser } from "./IServiceUser";
import type { SubQuestionDraft } from "@/component/question";

export enum AssessmentStatus {
  DRAFT = "draft",
  COMPLETED = "complete",
  PENDING = "pending",
  REVIEWED = "reviewed",
}

export interface SupportPlan {
  _id: string;
  serviceUser: ReviewedBy;
  custom: boolean;
  assessment: Assessment;
  reviewedBy: ReviewedBy;
  documents: Document[];
  notes: string;
  nextReviewDate: Date;
  status: string;
  riskAssessmentAttached: {
    riskAssessment: RiskAssessment | string;
    reviewedBy: ReviewedBy | Partial<Staff>;
  }[];
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  archived?: Archived;
}

export interface RiskAssessment {
  assessmentDate?: string | null;
  _id: string;
  serviceUser: ReviewedBy;
  custom: boolean;
  whatRisk: string;
  whoHarmed: string;
  likelihood: number;
  severity: number;
  riskScore: number;
  riskScoreText: string;
  actions: string;
  assessment: Assessment;
  reviewedBy: ReviewedBy;
  documents: any[];
  notes: string;
  nextReviewDate: Date;
  status: string;
  archived: Archived;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export interface Archived {
  value: boolean;
  by: string;
  date: Date;
  reason: string;
  _id: string;
}

export interface Assessment {
  title: string;
  icon: string;
  sections: Section[];
  _id?: string;
  createdAt?: Date;
  updatedAt?: Date;
  reviewedBy?: ReviewedBy;
}

export interface Question {
  nature?: string;
  questionType: string;
  questionText: string;
  questionSubtext: string;
  options: string[];
  answer: string | string[];
  isRequired: boolean;
  comment: string;
  _id: string;
  subQuestions: any[];
}

export interface Document {
  title: string;
  url: string;
  uploadedBy: ReviewedBy;
  _id?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ReviewedBy {
  _id: string;
  firstName: string;
  lastName: string;
  avatar: string;
  middleName: string;
  name: string;
  id: string;
  dateOfBirth?: Date;
  status?: string;
  age?: string | number;
}

export interface InitialAssessment {
  _id: string;
  serviceUser: CompactServiceUser;
  custom: boolean;
  refAssessmentId: string;
  assessment: Assessment;
  status: AssessmentStatus;
  __v: number;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: CompactServiceUser;
}

export interface Section {
  sectionTitle: string;
  questions: SectionQuestion[];
  _id?: string;
}

export interface SectionQuestion {
  nature?: string;
  questionType: QuestionType;
  questionText: string;
  questionSubtext: string;
  options: string[];
  answer: string[] | string;
  isRequired?: boolean;
  comment?: string;
  subQuestions?: SubQuestion[];
  _id?: string;
}

export enum QuestionType {
  MultipleChoice = "multiple-choice",
  SingleChoice = "single-choice",
  Text = "text",
}

export interface SubQuestion {
  questions: SubQuestionQuestion[];
  showOnOptionIndex: number;
  _id?: string;
}

export interface SubQuestionQuestion {
  questionType: QuestionType;
  questionText: string;
  questionSubtext: string;
  options: string[];
  isRequired: boolean | string;
  answer?: string[] | null | string;
  comment: string;
  note?: string;
}

export interface DocumentMetadata {
  title: string;
  category?: string;
  reviewDate?: string | null;
  file?: File;
  url?: string;
  name?: string;
  _id?: string;
  uploadedBy?: {
    firstName?: string;
    lastName?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomQuestion {
  id: string;
  type: QuestionType;
  questionText: string;
  subheading: string;
  comment: string;
  answer?: string | string[];
  options?: string[];
  subQuestions?: {
    showOnOptionIndex: number;
    questions: SubQuestionDraft[];
  }[];
}

export interface SupportPlanDataType {
  effectiveAt?: string | null;
  custom?: boolean;
  assessment?: {
    icon: any;
    title?: string;
    sections?: {
      sectionTitle: string;
      questions: Array<{
        comment: string;
        questionType: string;
        questionText: string;
        questionSubtext: string;
        options: any[];
        answer: string;
        subQuestions?: SubQuestion[];
      }>;
    }[];
  };
  sections?: Section[];
  notes?: string;
  documents?: any[];
  documentData?: DocumentMetadata[];
  riskAssessmentAttached?: SupportPlan["riskAssessmentAttached"];
  customQuestions?: CustomQuestion[];
  nextReviewDate?: string;
  icon?: string;
  serviceUser?: {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    name?: string;
    avatar?: string;
    dateOfBirth?: string;
    status?: string;
    age?: string;
  };
  archived?: {
    value: boolean;
    date: string;
  };
  reviewedBy?: {
    name?: string;
    _id: string;
    avatar: string;
    firstName: string;
    middleName: string;
    lastName: string;
  };
  createdAt?: string;
  updatedAt?: string;
  status?: string;
}
